/**
 * Fetches the cosign queue from Medplum: session notes (appointment-tagged QRs) and
 * chart documents that require supervisor cosign (treatment plans, discharge summaries).
 */
import { useCallback, useEffect, useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import type {
  Patient,
  Practitioner,
  Questionnaire,
  QuestionnaireResponse,
} from '@medplum/fhirtypes';
import { APPOINTMENT_TAG_SYSTEM, getReviewCommentsFromQR } from '../../appointments/fhirNoteMapping';
import { resolveQuestionnaireForResponseRef } from '@/lib/chart/chartQuestionnaireRef';
import { formatPractitionerStaffLabel } from '@unify/fhir-model/resources/practitioner';
import { toFhirDateOnlyLocal } from '@/lib/dateHelpers';
import { isChartDocumentQuestionnaireResponse } from '@/lib/chart/chartDocumentWorkflow';
import {
  getNotesQueueStatuses,
  isOwnUnsignedNotesQueue,
  OWN_UNSIGNED_DOCUMENT_TYPES,
  qrToDocumentReviewStatus,
  questionnaireToDocumentType,
  questionnaireToMissingDocumentFormType,
  type SupervisionNotesQueue,
} from '../utils/questionnaireResponseMappings';
import {
  hasAppointmentDocumentationTag,
  isAwaitingSupervisorCosign,
  isCosignQueueSourceDocument,
} from '../utils/cosignQueueEligibility';
import { fetchCosignQueueQuestionnaireResponses } from '../utils/fetchCosignQueueQuestionnaireResponses';
import {
  fetchMissingProgressNotesFromAppointments,
  missingProgressNoteToQueueItem,
} from '../utils/missingDocumentsFromAppointments';
import type { SupervisionQueueItemWithDocumentId } from '../types';
import { useSupervisionScope } from './useSupervisionScope';
import { getUserFacingErrorMessage } from '@/lib/userFacingError';

const COSIGN_QUEUE_PAGE_SIZE = 100;

function extractPatientId(ref: string | undefined): string | undefined {
  if (!ref) return undefined;
  const direct = ref.match(/^Patient\/([^/]+)$/);
  if (direct) return direct[1];
  return ref.match(/Patient\/([^/]+)/)?.[1];
}

function extractPractitionerId(
  authorRef: string | undefined,
  practitioner: Practitioner | null,
): string | undefined {
  if (practitioner?.id) return practitioner.id;
  if (!authorRef) return undefined;
  const direct = authorRef.match(/^Practitioner\/([^/]+)$/);
  if (direct) return direct[1];
  return authorRef.match(/Practitioner\/([^/]+)/)?.[1];
}

function formatHumanName(name: { given?: string[]; family?: string; prefix?: string[] } | undefined): string {
  if (!name) return '—';
  const given = name.given?.join(' ') ?? '';
  const family = name.family ?? '';
  const prefix = name.prefix?.join(' ') ?? '';
  const parts = [prefix, `${given} ${family}`.trim()].filter(Boolean);
  return parts.join(' ') || '—';
}

function getPatientDisplayName(patient: Patient | null): string {
  if (!patient) return '—';
  const preferred = patient.name?.find((n) => n.use === 'usual');
  const name = preferred ?? patient.name?.[0];
  return formatHumanName(name);
}

/** Prefer the loaded Patient; if that read failed, use QR.subject display/id already on the note. */
export function resolveCosignQueueClient(
  qr: { subject?: { reference?: string; display?: string } },
  patient: Patient | null,
): { clientId?: string; clientName: string } {
  const fromPatient = getPatientDisplayName(patient);
  const fromSubject = qr.subject?.display?.trim();
  const clientName = fromPatient !== '—' ? fromPatient : (fromSubject || '—');
  const clientId = patient?.id ?? extractPatientId(qr.subject?.reference);
  return { clientId, clientName };
}

function getPractitionerDisplayName(practitioner: Practitioner | null): string {
  if (!practitioner) return '—';
  return formatPractitionerStaffLabel(practitioner) || '—';
}

export interface UseCosignQueueFromMedplumResult {
  items: SupervisionQueueItemWithDocumentId[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export interface UseCosignQueueFromMedplumOptions {
  /** `own-unsigned`: viewer's own draft notes. Default follows the active role (cosign vs provider unsigned). */
  queue?: SupervisionNotesQueue;
}

export function useCosignQueueFromMedplum(
  options?: UseCosignQueueFromMedplumOptions,
): UseCosignQueueFromMedplumResult {
  const queue: SupervisionNotesQueue = options?.queue ?? 'role-default';
  const medplum = useMedplum();
  const {
    scopeReady,
    isClinicianInScope,
    role,
    viewerPractitionerId,
    superviseePractitionerIds,
  } = useSupervisionScope();
  const [items, setItems] = useState<SupervisionQueueItemWithDocumentId[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const doFetch = useCallback(async (): Promise<void> => {
    if (!scopeReady) {
      setLoading(true);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const responses = await fetchCosignQueueQuestionnaireResponses(medplum, {
        role,
        viewerPractitionerId,
        superviseePractitionerIds,
        fallbackPageSize: COSIGN_QUEUE_PAGE_SIZE,
        ...(queue === 'own-unsigned'
          ? { authorIds: viewerPractitionerId ? [viewerPractitionerId] : [] }
          : {}),
      });

      const potentialCandidates = responses.filter(
        (qr: QuestionnaireResponse) =>
          hasAppointmentDocumentationTag(qr) || isChartDocumentQuestionnaireResponse(qr),
      );

      const questionnaireRefs = new Set<string>();
      for (const qr of potentialCandidates) {
        if (qr.questionnaire) questionnaireRefs.add(qr.questionnaire);
      }

      const questionnaireMap = new Map<string, Questionnaire | null>();
      await Promise.all(
        [...questionnaireRefs].map(async (ref) => {
          const q = await resolveQuestionnaireForResponseRef(medplum, ref);
          questionnaireMap.set(ref, q ?? null);
        }),
      );

      const cosignCandidates = potentialCandidates.filter((qr: QuestionnaireResponse) =>
        isCosignQueueSourceDocument(
          qr,
          qr.questionnaire ? questionnaireMap.get(qr.questionnaire) ?? null : null,
        ),
      );

      const subjectRefs = new Set<string>();
      const authorRefs = new Set<string>();

      for (const qr of cosignCandidates) {
        if (qr.subject?.reference) subjectRefs.add(qr.subject.reference);
        if (qr.author?.reference) authorRefs.add(qr.author.reference);
      }

      const patientMap = new Map<string, Patient | null>();
      await Promise.all(
        [...subjectRefs]
          .filter((r) => r.startsWith('Patient/'))
          .map(async (ref) => {
            const id = extractPatientId(ref);
            if (!id) return;
            try {
              const p = await medplum.readResource('Patient', id);
              patientMap.set(ref, p);
            } catch {
              patientMap.set(ref, null);
            }
          }),
      );

      const practitionerMap = new Map<string, Practitioner | null>();
      await Promise.all(
        [...authorRefs]
          .filter((r) => r.startsWith('Practitioner/'))
          .map(async (ref) => {
            const id = extractPractitionerId(ref, null);
            if (!id) return;
            try {
              const p = await medplum.readResource('Practitioner', id);
              practitionerMap.set(ref, p);
            } catch {
              practitionerMap.set(ref, null);
            }
          }),
      );

      const queueItems: SupervisionQueueItemWithDocumentId[] = cosignCandidates.map(
        (qr: QuestionnaireResponse) => {
          const questionnaire = qr.questionnaire
            ? questionnaireMap.get(qr.questionnaire) ?? null
            : null;
          const documentType = questionnaireToDocumentType(questionnaire);
          const missingDocumentFormType = questionnaireToMissingDocumentFormType(questionnaire);
          const status = qrToDocumentReviewStatus(qr);
          const subjectRef = qr.subject?.reference;
          const authorRef = qr.author?.reference;
          const patient = subjectRef ? patientMap.get(subjectRef) ?? null : null;
          const practitioner = authorRef ? practitionerMap.get(authorRef) ?? null : null;
          const { clientId, clientName } = resolveCosignQueueClient(qr, patient);
          const fromPractitioner = getPractitionerDisplayName(practitioner);
          const clinicianName =
            fromPractitioner !== '—' ? fromPractitioner : (qr.author?.display?.trim() || '—');
          const clinicianId =
            extractPractitionerId(authorRef, practitioner) ?? `qr-${qr.id}`;
          const authored = qr.authored ?? qr.meta?.lastUpdated;
          const dueDate = authored ? toFhirDateOnlyLocal(new Date(authored)) : undefined;
          const isOverdue = dueDate ? dueDate < toFhirDateOnlyLocal(new Date()) : false;

          const title =
            questionnaire?.title ?? questionnaire?.name ?? 'Document';
          const titleSuffix = clientName !== '—' ? ` – ${clientName}` : '';
          const commentCount = getReviewCommentsFromQR(qr).length;

          return {
            id: qr.id!,
            documentType,
            missingDocumentFormType,
            title: `${title}${titleSuffix}`,
            clinicianId,
            clinicianName,
            clientId,
            clientName,
            status,
            riskLevel: 'low',
            dueDate,
            submittedAt: authored,
            isOverdue,
            commentCount: Number(commentCount) || 0,
            version: qr.meta?.versionId ? parseInt(qr.meta.versionId, 10) : undefined,
            appointmentId: qr.meta?.tag?.find((t) => t.system === APPOINTMENT_TAG_SYSTEM)?.code,
            documentIdForReview: qr.id!,
          };
        },
      );

      const allowedStatuses = getNotesQueueStatuses(role, queue);
      const ownUnsigned = isOwnUnsignedNotesQueue(role, queue);
      let filtered = queueItems.filter((item) => {
        if (!allowedStatuses.includes(item.status)) return false;
        if (ownUnsigned) {
          if (!viewerPractitionerId || item.clinicianId !== viewerPractitionerId) return false;
          if (!OWN_UNSIGNED_DOCUMENT_TYPES.includes(item.documentType)) return false;
        } else if (!isClinicianInScope(item.clinicianId)) {
          return false;
        }

        const qr = cosignCandidates.find((candidate) => candidate.id === item.id);
        if (!qr) return false;

        const questionnaire = qr.questionnaire
          ? questionnaireMap.get(qr.questionnaire) ?? null
          : null;
        const authorRef = qr.author?.reference;
        const practitioner = authorRef ? practitionerMap.get(authorRef) ?? null : null;

        if (queue === 'role-default' && role === 'supervisor' && item.status === 'signed') {
          return isAwaitingSupervisorCosign(qr, questionnaire, practitioner, item.status, {
            authorIsKnownSupervisee: superviseePractitionerIds.includes(item.clinicianId),
          });
        }
        return true;
      });

      if (ownUnsigned && viewerPractitionerId) {
        const missingNotes = await fetchMissingProgressNotesFromAppointments(medplum, [
          viewerPractitionerId,
        ]);
        const appointmentIdsWithQr = new Set(
          filtered.map((item) => item.appointmentId).filter(Boolean),
        );
        const missingQueueItems = missingNotes
          .filter((item) => item.appointmentId && !appointmentIdsWithQr.has(item.appointmentId))
          .map(missingProgressNoteToQueueItem);
        filtered = [...filtered, ...missingQueueItems];
      }

      setItems(filtered);
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Failed to load cosign queue'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [medplum, scopeReady, isClinicianInScope, role, viewerPractitionerId, superviseePractitionerIds, queue]);

  useEffect(() => {
    doFetch();
  }, [doFetch]);

  return { items, loading, error, refetch: doFetch };
}
